import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { rupiah, shortDate } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Search, Pencil, Trash2, UserPlus, Save } from "lucide-react";

export default function Customers() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // customer or {new: true}
  const [form, setForm] = useState({ name: "", phone: "", email: "", notes: "" });

  const load = async () => {
    const { data } = await api.get("/customers", { params: { q } });
    setList(data);
  };
  useEffect(() => { const t = setTimeout(load, 150); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const openEdit = (c) => {
    setForm({ name: c.name || "", phone: c.phone || "", email: c.email || "", notes: c.notes || "" });
    setEditing(c);
  };
  const openNew = () => {
    setForm({ name: "", phone: "", email: "", notes: "" });
    setEditing({ new: true });
  };
  const del = async (c) => {
    if (!window.confirm(`Hapus customer "${c.name}"? Invoice terkait tetap ada.`)) return;
    await api.delete(`/customers/${c.id}`);
    toast.success("Customer dihapus");
    load();
  };
  const save = async () => {
    if (!form.name.trim()) return toast.error("Nama wajib diisi");
    try {
      if (editing?.new) {
        await api.post("/customers", form);
        toast.success("Customer ditambahkan");
      } else {
        await api.put(`/customers/${editing.id}`, form);
        toast.success("Customer diperbarui");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal simpan");
    }
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">Customers</h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">Database pelanggan — otomatis terisi dari invoice, bisa diedit manual.</p>
        </div>
        <Button onClick={openNew} className="bg-[#556b2f] hover:bg-[#3d4d1e]" data-testid="cust-new-btn"><UserPlus size={16} className="mr-1"/> Tambah Customer</Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4">
            <Search size={16} className="text-slate-400"/>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama pelanggan..." className="max-w-md" data-testid="cust-search"/>
          </div>

          {/* Mobile: cards */}
          <div className="md:hidden space-y-3" data-testid="cust-mobile">
            {list.map((c) => (
              <div key={c.id} className="border border-slate-200 rounded-lg p-3 bg-white">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 truncate"><Link to={`/customers/${c.id}`} className="hover:text-[#556b2f]">{c.name}</Link></div>
                    <div className="text-xs text-slate-500 truncate">{c.phone || "-"} · {c.email || "-"}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`cust-edit-${c.id}`}><Pencil size={14}/></Button>
                    <Button size="sm" variant="ghost" onClick={() => del(c)} data-testid={`cust-del-${c.id}`}><Trash2 size={14} className="text-red-600"/></Button>
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                  <div><div className="text-slate-400">Trx</div><div className="mono font-semibold">{c.total_transactions || 0}</div></div>
                  <div className="col-span-2"><div className="text-slate-400">Total</div><div className="mono font-semibold text-[#556b2f]">{rupiah(c.total_amount || 0)}</div></div>
                </div>
              </div>
            ))}
            {list.length === 0 && <div className="text-center text-slate-400 py-10">Belum ada pelanggan.</div>}
          </div>

          {/* Desktop: table */}
          <div className="hidden md:block overflow-x-auto">
            <Table className="min-w-[1100px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Nama</TableHead>
                  <TableHead>No HP</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="text-right">Transaksi</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Airline</TableHead>
                  <TableHead>Terakhir Terbang</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.map((c) => (
                  <TableRow key={c.id} data-testid={`cust-row-${c.id}`}>
                    <TableCell className="font-medium"><Link to={`/customers/${c.id}`} className="hover:text-[#556b2f] hover:underline">{c.name}</Link></TableCell>
                    <TableCell>{c.phone || "-"}</TableCell>
                    <TableCell>{c.email || "-"}</TableCell>
                    <TableCell className="text-right font-semibold mono">{c.total_transactions}</TableCell>
                    <TableCell className="text-right mono">{rupiah(c.total_amount || 0)}</TableCell>
                    <TableCell className="text-slate-500">{(c.airlines || []).slice(0,2).join(", ")}</TableCell>
                    <TableCell>{shortDate(c.last_flight)}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`cust-edit-${c.id}`}><Pencil size={14}/></Button>
                      <Button size="sm" variant="ghost" onClick={() => del(c)} data-testid={`cust-del-${c.id}`}><Trash2 size={14} className="text-red-600"/></Button>
                    </TableCell>
                  </TableRow>
                ))}
                {list.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-10">Belum ada pelanggan.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="cust-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.new ? "Tambah Customer" : "Edit Customer"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Nama *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cust-form-name"/></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>No HP</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="628123..." data-testid="cust-form-phone"/></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cust-form-email"/></div>
            </div>
            <div><Label>Catatan</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}/></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Batal</Button>
            <Button onClick={save} className="bg-[#556b2f] hover:bg-[#3d4d1e]" data-testid="cust-form-save"><Save size={14} className="mr-1"/> Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
